/**
 * The NOVA asset catalog — the one typed list of everything a coach can own.
 *
 * Today the same sixteen assets are described in five places (the deliverable
 * type union, the phase room maps, the skill display metadata, the skill output
 * map, and the persona labels), and every surface re-derives stage, label and
 * order from whichever of those it happens to import. This module is the single
 * definition: what the asset is called, where it sits, what it costs to reach,
 * what it depends on, how it opens, and where the evidence that it exists
 * lives.
 *
 * Pure: it imports only other pure modules and types, so it is safe in
 * a route, a component, a server gate, or a test.
 *
 * Aliases: asset catalog, nova assets, my assets, asset entries
 */

import type { NamedFeatureKey } from "~/lib/shared/features/keys";
import { READINESS_SCORE_NAME } from "~/lib/shared/readiness/config";
import { READINESS_PAGE_PATH } from "~/lib/shared/readiness/routes";

import { isCodeStampedDeliverableType } from "../phases";
import type { PlanStepName } from "../plan-steps";
import type { NovaRoutineKind } from "../routines";
import type { SkillName } from "../skill-metadata";
import type { NovaAgentType, NovaDeliverableType } from "../types";
import { WEEKLY_CHECK_IN_PATH } from "../weekly-check-in";

/** The four stages, in the order the coach walks them. */
export type AssetStage = "diagnose" | "plan" | "build" | "grow";

/** Sort weight per stage. Catalog order is `(ASSET_STAGE_ORDER[stage], order)`. */
export const ASSET_STAGE_ORDER: Record<AssetStage, number> = {
  diagnose: 0,
  plan: 1,
  build: 2,
  grow: 3,
};

/** Which page the asset renders on: My assets, or My content. */
export type AssetSurface = "assets" | "content";

/**
 * Whether the asset is one living record or a growing set. A singleton's status
 * reads off one row; a collection's reads off its members.
 */
export type AssetCardinality = "singleton" | "collection";

/**
 * The lowest lifecycle that opens the asset. The trial opens everything a paid
 * plan does, so there is no `paid` minimum. `churned` is never a minimum
 * (nothing is unlocked BY churning), and no asset depends on the coach's tier
 * today, so the tier is deliberately absent.
 */
export type AssetMinimumAccess = "pre_trial" | "trial";

/** A Client Acquisition System component — the course's grouping of assets. */
export type CasComponentKey =
  | "positioning"
  | "offer"
  | "mini_sprint"
  | "profiles"
  | "outreach"
  | "ties_together"
  | "after_course";

export interface CasComponent {
  readonly label: string;
  /**
   * Week of the Business Builder Course that produces this component. Null for
   * the two unnumbered groups, which sit outside the five-week sequence.
   */
  readonly courseWeek: number | null;
}

/**
 * The component table. The week lives here rather than on every entry so a
 * component's assets cannot disagree about which week they belong to.
 */
export const CAS_COMPONENTS = {
  positioning: { label: "Positioning", courseWeek: 1 },
  offer: { label: "The Offer", courseWeek: 2 },
  mini_sprint: { label: "The Mini Sprint", courseWeek: 3 },
  profiles: { label: "The Profiles", courseWeek: 4 },
  outreach: { label: "The Outreach", courseWeek: 5 },
  ties_together: { label: "Ties it together", courseWeek: null },
  after_course: { label: "After the program", courseWeek: null },
} as const satisfies Record<CasComponentKey, CasComponent>;

/**
 * How the coach opens the asset.
 *
 * `agent` is the honest temporary form for the 90-day plan. There is no
 * `ninety-day-plan` skill: the plan is written by the `grow` AGENT, started
 * today as `createNovaConversation({ agentType: "grow" })` with
 * `GROW_KICKOFF_PROMPT` auto-sent. Registering that prompt as a skill is
 * planned but not done, so the catalog names what exists rather than a skill
 * that does not. The variant disappears when the skill lands.
 */
export type AssetOpens =
  | { readonly kind: "page"; readonly route: string }
  | { readonly kind: "plan_step"; readonly step: PlanStepName }
  | { readonly kind: "skill"; readonly skill: SkillName }
  | { readonly kind: "agent"; readonly agent: NovaAgentType }
  | { readonly kind: "none" };

/** Where the evidence that the asset exists — and is approved — lives. */
export type AssetTracks =
  | { readonly kind: "deliverable"; readonly type: NovaDeliverableType }
  | { readonly kind: "plan_step"; readonly step: PlanStepName }
  | { readonly kind: "assessment" }
  | { readonly kind: "articles" }
  | { readonly kind: "stories" }
  /**
   * A NOVA routine she runs rather than an asset she builds: open while she
   * has routines (a paid plan and the routines flag), never on the build path
   * and never approved.
   */
  | { readonly kind: "routine"; readonly routine: NovaRoutineKind }
  | { readonly kind: "none" };

/**
 * One catalog row, generic over its own key type so `dependsOn` can be narrowed
 * to real keys once the literal list below exists. Consumers use
 * {@link AssetCatalogEntry}, the resolved form.
 */
interface AssetCatalogEntryOf<TKey extends string> {
  readonly key: TKey;
  readonly label: string;
  readonly stage: AssetStage;
  /** Sort position WITHIN the stage. Unique per stage. */
  readonly order: number;
  readonly surface: AssetSurface;
  /** Client Acquisition System component, or null when the asset is outside it. */
  readonly component: CasComponentKey | null;
  readonly estimateMinutes: number | null;
  /** Dependencies, expressed on APPROVAL of the named entries. */
  readonly dependsOn: readonly TKey[];
  readonly minimumAccess: AssetMinimumAccess;
  readonly comingSoon: boolean;
  /**
   * The feature the card stands for while it is Coming soon. The live check
   * refuses to move that feature to Beta or New while `comingSoon` is on, so
   * shipping the card comes first.
   */
  readonly featureKey: NamedFeatureKey | null;
  readonly cardinality: AssetCardinality;
  readonly opens: AssetOpens;
  readonly tracks: AssetTracks;
  /**
   * Canned opening message for a deterministic start. Null for pages, and for a
   * `plan_step` entry that shares its door with another entry — the step's own
   * kickoff opens it, so a second starter would never be sent.
   */
  readonly starterPrompt: string | null;
  /** The asset also renders as a What NOVA knows record. */
  readonly knowsRecord: boolean;
}

/**
 * Declared in catalog order — `(stage, order)` — so `ASSET_CATALOG` itself is
 * the canonical sequence and no consumer has to sort. A test enforces it.
 */
const CATALOG = [
  // ── Diagnose ──────────────────────────────────────────────────────────────
  {
    key: "six_figure_score",
    label: READINESS_SCORE_NAME,
    stage: "diagnose",
    order: 0,
    surface: "assets",
    component: "ties_together",
    estimateMinutes: 10,
    dependsOn: [],
    minimumAccess: "pre_trial",
    comingSoon: false,
    featureKey: null,
    cardinality: "singleton",
    opens: { kind: "page", route: READINESS_PAGE_PATH },
    tracks: { kind: "assessment" },
    starterPrompt: null,
    knowsRecord: false,
  },
  {
    key: "north_star",
    label: "North Star",
    stage: "diagnose",
    order: 1,
    surface: "assets",
    component: "ties_together",
    estimateMinutes: 5,
    dependsOn: [],
    minimumAccess: "trial",
    comingSoon: false,
    featureKey: null,
    cardinality: "singleton",
    // Shares the Identity Architect door with the identity statement: the
    // ambition is code-stamped inside that step by `save_ambition`, so there is
    // no separate place to send the coach — and no starter of its own, because
    // the step's own kickoff is what opens that door (both cards send the coach
    // into the same conversation).
    opens: { kind: "plan_step", step: "identity_architect" },
    tracks: { kind: "deliverable", type: "ambition_level" },
    starterPrompt: null,
    knowsRecord: true,
  },

  // ── Plan ──────────────────────────────────────────────────────────────────
  {
    key: "voice_profile",
    label: "Your voice",
    stage: "plan",
    order: 0,
    surface: "assets",
    component: null,
    estimateMinutes: 15,
    dependsOn: [],
    minimumAccess: "trial",
    comingSoon: false,
    featureKey: null,
    cardinality: "singleton",
    opens: { kind: "skill", skill: "voice-onboarding" },
    tracks: { kind: "deliverable", type: "voice_profile" },
    starterPrompt: "Set up my voice profile and channel guides.",
    knowsRecord: true,
  },
  {
    key: "identity_statement",
    label: "Identity statement",
    stage: "plan",
    order: 1,
    surface: "assets",
    component: "positioning",
    estimateMinutes: 30,
    dependsOn: ["north_star"],
    minimumAccess: "trial",
    comingSoon: false,
    featureKey: null,
    cardinality: "singleton",
    opens: { kind: "plan_step", step: "identity_architect" },
    tracks: { kind: "deliverable", type: "identity_statement" },
    starterPrompt: "Let's define the coach I'm becoming.",
    knowsRecord: true,
  },
  {
    key: "ideal_client_profile",
    label: "Ideal client profile",
    stage: "plan",
    order: 2,
    surface: "assets",
    component: "positioning",
    estimateMinutes: 45,
    dependsOn: ["identity_statement"],
    minimumAccess: "trial",
    comingSoon: false,
    featureKey: null,
    cardinality: "singleton",
    opens: { kind: "plan_step", step: "icp_definition" },
    tracks: { kind: "deliverable", type: "icp_definition" },
    starterPrompt: "Let's define exactly who I'm for.",
    knowsRecord: true,
  },
  {
    key: "niche_selection",
    label: "Niche selection",
    stage: "plan",
    order: 3,
    surface: "assets",
    component: "positioning",
    estimateMinutes: 20,
    dependsOn: ["ideal_client_profile"],
    minimumAccess: "trial",
    comingSoon: false,
    featureKey: null,
    cardinality: "singleton",
    opens: { kind: "plan_step", step: "market_check" },
    // No deliverable of its own: the verdict is stamped onto the latest ICP
    // row, so completion is derived from the plan step, never from a queue row.
    tracks: { kind: "plan_step", step: "market_check" },
    starterPrompt: "Let's pressure-test my ideal client against the market.",
    knowsRecord: false,
  },
  {
    key: "core_offer",
    label: "Core Coaching Offer",
    stage: "plan",
    order: 4,
    surface: "assets",
    component: "offer",
    estimateMinutes: 60,
    dependsOn: ["niche_selection"],
    minimumAccess: "trial",
    comingSoon: false,
    featureKey: null,
    cardinality: "singleton",
    opens: { kind: "plan_step", step: "core_offer_design" },
    tracks: { kind: "deliverable", type: "core_offer" },
    starterPrompt: "Let's shape my core offer.",
    knowsRecord: false,
  },
  {
    key: "numbers_calculator",
    label: "Numbers calculator",
    stage: "plan",
    order: 5,
    surface: "assets",
    component: null,
    estimateMinutes: null,
    dependsOn: [],
    minimumAccess: "trial",
    comingSoon: true,
    featureKey: "numbers_calculator",
    cardinality: "singleton",
    opens: { kind: "none" },
    tracks: { kind: "none" },
    starterPrompt: null,
    knowsRecord: false,
  },

  // ── Build ─────────────────────────────────────────────────────────────────
  {
    key: "dcm_profile",
    label: "Dream Coach Match profile",
    stage: "build",
    order: 0,
    surface: "assets",
    component: "profiles",
    estimateMinutes: 20,
    dependsOn: ["core_offer", "voice_profile"],
    minimumAccess: "trial",
    comingSoon: false,
    featureKey: null,
    cardinality: "singleton",
    opens: { kind: "skill", skill: "profile-update" },
    tracks: { kind: "deliverable", type: "profile_update" },
    starterPrompt: "Help me update my public coach profile.",
    knowsRecord: false,
  },
  {
    key: "linkedin_profile",
    label: "LinkedIn profile",
    stage: "build",
    order: 1,
    surface: "assets",
    component: "profiles",
    estimateMinutes: 25,
    // No `core_offer` dependency: the `linkedin-profile` skill's own
    // `requires` is voice + ICP, no offer, and the card must agree with it so
    // the coach is never refused at activation.
    dependsOn: ["ideal_client_profile", "voice_profile"],
    minimumAccess: "trial",
    comingSoon: false,
    featureKey: null,
    cardinality: "singleton",
    opens: { kind: "skill", skill: "linkedin-profile" },
    tracks: { kind: "deliverable", type: "linkedin_profile" },
    starterPrompt: "Help me rewrite my LinkedIn profile.",
    knowsRecord: false,
  },
  {
    key: "mini_sprint",
    label: "Mini Sprint",
    stage: "build",
    order: 2,
    surface: "assets",
    component: "mini_sprint",
    estimateMinutes: 60,
    dependsOn: ["core_offer", "voice_profile"],
    minimumAccess: "trial",
    comingSoon: false,
    featureKey: null,
    cardinality: "singleton",
    opens: { kind: "skill", skill: "mini-sprint" },
    tracks: { kind: "deliverable", type: "mini_sprint" },
    starterPrompt: "Help me design my Mini Sprint.",
    knowsRecord: false,
  },
  {
    key: "outreach_scripts",
    label: "Outreach scripts",
    stage: "build",
    order: 3,
    surface: "assets",
    component: "outreach",
    estimateMinutes: 30,
    dependsOn: ["core_offer", "voice_profile"],
    minimumAccess: "trial",
    comingSoon: false,
    featureKey: null,
    cardinality: "singleton",
    opens: { kind: "skill", skill: "outreach-scripts" },
    tracks: { kind: "deliverable", type: "outreach_scripts" },
    starterPrompt: "Help me draft my outreach scripts.",
    knowsRecord: false,
  },
  {
    key: "wheel_page_copy",
    label: "Your Wheel of Life page",
    stage: "build",
    order: 4,
    surface: "assets",
    component: null,
    estimateMinutes: 15,
    // The skill also requires the ideal client profile, which the core offer
    // already depends on through the niche check.
    dependsOn: ["core_offer", "voice_profile"],
    minimumAccess: "trial",
    comingSoon: false,
    featureKey: null,
    cardinality: "singleton",
    opens: { kind: "skill", skill: "wheel-page-copy" },
    tracks: { kind: "deliverable", type: "wheel_page_copy" },
    starterPrompt: "Help me write my Wheel of Life page text.",
    knowsRecord: false,
  },
  {
    key: "social_images",
    label: "Images for social posts",
    stage: "build",
    order: 5,
    surface: "assets",
    component: null,
    estimateMinutes: null,
    dependsOn: [],
    minimumAccess: "trial",
    comingSoon: true,
    featureKey: "social_images",
    cardinality: "collection",
    opens: { kind: "none" },
    tracks: { kind: "none" },
    starterPrompt: null,
    knowsRecord: false,
  },
  {
    key: "instagram_posts",
    label: "Instagram posts",
    stage: "build",
    order: 6,
    surface: "assets",
    component: null,
    estimateMinutes: null,
    dependsOn: [],
    minimumAccess: "trial",
    comingSoon: true,
    featureKey: "instagram_posts",
    cardinality: "collection",
    opens: { kind: "none" },
    tracks: { kind: "none" },
    starterPrompt: null,
    knowsRecord: false,
  },

  // ── Build, My content surface ─────────────────────────────────────────────
  {
    key: "articles",
    label: "Articles",
    stage: "build",
    order: 10,
    surface: "content",
    component: null,
    estimateMinutes: 45,
    dependsOn: ["voice_profile"],
    minimumAccess: "trial",
    comingSoon: false,
    featureKey: null,
    cardinality: "collection",
    opens: { kind: "skill", skill: "cih-article" },
    tracks: { kind: "articles" },
    starterPrompt: "Help me draft a coaching article.",
    knowsRecord: false,
  },
  {
    key: "linkedin_posts",
    label: "LinkedIn posts",
    stage: "build",
    order: 11,
    surface: "content",
    component: null,
    estimateMinutes: 15,
    dependsOn: ["voice_profile", "core_offer"],
    minimumAccess: "trial",
    comingSoon: false,
    featureKey: null,
    cardinality: "collection",
    opens: { kind: "skill", skill: "linkedin-post" },
    tracks: { kind: "deliverable", type: "linkedin_post" },
    starterPrompt: "Help me write a LinkedIn post.",
    knowsRecord: false,
  },
  {
    key: "client_stories",
    label: "Client stories",
    stage: "build",
    order: 12,
    surface: "content",
    component: null,
    estimateMinutes: 20,
    dependsOn: ["voice_profile"],
    minimumAccess: "trial",
    comingSoon: false,
    featureKey: null,
    cardinality: "collection",
    opens: { kind: "skill", skill: "transformation-story" },
    tracks: { kind: "stories" },
    starterPrompt: "Help me draft a client testimonial for my coach profile.",
    knowsRecord: false,
  },

  // ── Grow ──────────────────────────────────────────────────────────────────
  {
    key: "ninety_day_plan",
    label: "90-day plan",
    stage: "grow",
    order: 0,
    surface: "assets",
    component: "after_course",
    estimateMinutes: 45,
    // The ICP is as load-bearing as the offer: the plan's outreach targets and
    // channels are written from who the coach is for, and the grow agent's own
    // grounding checks look for it. Transitively required through the niche
    // check today, so naming it here only makes the gate explicit.
    dependsOn: ["ideal_client_profile", "core_offer"],
    minimumAccess: "trial",
    comingSoon: false,
    featureKey: null,
    cardinality: "singleton",
    opens: { kind: "agent", agent: "grow" },
    tracks: { kind: "deliverable", type: "ninety_day_plan" },
    starterPrompt: "Let's build my 90-day plan.",
    knowsRecord: false,
  },
  {
    key: "proven_play",
    label: "Proven Play",
    stage: "grow",
    order: 1,
    surface: "assets",
    component: null,
    estimateMinutes: null,
    dependsOn: [],
    minimumAccess: "trial",
    comingSoon: true,
    featureKey: "proven_play",
    cardinality: "singleton",
    opens: { kind: "none" },
    tracks: { kind: "none" },
    starterPrompt: null,
    knowsRecord: false,
  },
  {
    key: "weekly_review",
    label: "Weekly review and check-ins",
    stage: "grow",
    order: 2,
    surface: "assets",
    component: null,
    estimateMinutes: null,
    dependsOn: [],
    minimumAccess: "trial",
    comingSoon: false,
    featureKey: "weekly_review",
    cardinality: "singleton",
    opens: { kind: "page", route: WEEKLY_CHECK_IN_PATH },
    tracks: { kind: "routine", routine: "weekly_check_in" },
    starterPrompt: null,
    knowsRecord: false,
  },
] as const satisfies readonly AssetCatalogEntryOf<string>[];

/** Every asset key, as a literal union. */
export type AssetKey = (typeof CATALOG)[number]["key"];

/** A catalog row with `dependsOn` narrowed to real keys. */
export type AssetCatalogEntry = AssetCatalogEntryOf<AssetKey>;

/**
 * The catalog, in catalog order. The annotation is what checks `dependsOn`:
 * a key that is not an `AssetKey` fails this assignment at build time.
 */
export const ASSET_CATALOG: readonly AssetCatalogEntry[] = CATALOG;

/**
 * Every asset a course week can build: a coming-soon one could never be
 * approved, a `surface: "content"` entry (articles, LinkedIn posts, client
 * stories) keeps no approval history for a week's completion to rest on, and
 * a routine is run every week, never built.
 */
export const COURSE_BUILDABLE_ASSETS: readonly AssetCatalogEntry[] =
  ASSET_CATALOG.filter(
    (asset) =>
      !asset.comingSoon &&
      asset.surface === "assets" &&
      asset.tracks.kind !== "routine",
  );

const BY_KEY: ReadonlyMap<AssetKey, AssetCatalogEntry> = new Map(
  ASSET_CATALOG.map((entry) => [entry.key, entry]),
);

/** Looks up an entry. Total over `AssetKey`, so it never returns undefined. */
export function assetByKey(key: AssetKey): AssetCatalogEntry {
  const entry = BY_KEY.get(key);
  if (!entry) throw new Error(`Unknown asset key: ${key}`);
  return entry;
}

/** Narrows an untrusted string to a catalog key — the validation boundary. */
export function isAssetKey(value: string): value is AssetKey {
  return BY_KEY.has(value as AssetKey);
}

/**
 * The entry a skill opens, in catalog order, or null when the skill has no
 * card (`NOT_IN_CATALOG.skills`). Built once so a deterministic start and the
 * chat's auto-send read the same starter prompt from the same row.
 */
const BY_SKILL: ReadonlyMap<SkillName, AssetCatalogEntry> = new Map(
  ASSET_CATALOG.flatMap((entry) =>
    entry.opens.kind === "skill" ? [[entry.opens.skill, entry] as const] : [],
  ),
);

export function assetBySkill(skill: SkillName): AssetCatalogEntry | null {
  return BY_SKILL.get(skill) ?? null;
}

/**
 * The entry whose EVIDENCE is a deliverable of this type, or null when the type
 * has no card (`NOT_IN_CATALOG.deliverableTypes`). Built from `tracks`, not
 * `opens`: the access gate is asked about a stored row, and `tracks` is what
 * says which asset that row is.
 */
const BY_DELIVERABLE_TYPE: ReadonlyMap<string, AssetCatalogEntry> = new Map(
  ASSET_CATALOG.flatMap((entry) =>
    entry.tracks.kind === "deliverable"
      ? [[entry.tracks.type as string, entry] as const]
      : [],
  ),
);

export function assetByDeliverableType(type: string): AssetCatalogEntry | null {
  return BY_DELIVERABLE_TYPE.get(type) ?? null;
}

/**
 * True when rows of this deliverable type make up a collection: each lineage is
 * one item (one LinkedIn post), so saving, editing and reviewing work per
 * lineage, not per type.
 */
export function isCollectionDeliverableType(type: string): boolean {
  return assetByDeliverableType(type)?.cardinality === "collection";
}

/** Every deliverable type whose rows make up a collection. */
export const COLLECTION_DELIVERABLE_TYPES: readonly string[] = [
  ...BY_DELIVERABLE_TYPE.keys(),
].filter(isCollectionDeliverableType);

/** The collections that exist today: LinkedIn posts, articles, client stories. */
export type CollectionAssetKey = Extract<
  (typeof CATALOG)[number],
  { readonly cardinality: "collection"; readonly comingSoon: false }
>["key"];

export function isCollectionAssetKey(key: AssetKey): key is CollectionAssetKey {
  const entry = assetByKey(key);
  return entry.cardinality === "collection" && !entry.comingSoon;
}

/** The collections on My content, in the order the page shows them. */
export const CONTENT_ASSET_KEYS = [
  "articles",
  "linkedin_posts",
  "client_stories",
] as const satisfies readonly CollectionAssetKey[];

export type ContentAssetKey = (typeof CONTENT_ASSET_KEYS)[number];

export function isContentAssetKey(key: string): key is ContentAssetKey {
  return (CONTENT_ASSET_KEYS as readonly string[]).includes(key);
}

/**
 * The asset opens on a page of its own: one living record on My assets whose
 * versions are a deliverable lineage or a Plan step's derived answer, at
 * `/my-coaching/business/nova/assets/$assetKey`, or a collection, whose items My
 * content lists under its type. The score keeps the page the catalog names
 * for it, and coming-soon entries have nothing to show yet.
 */
export function hasAssetPage(entry: AssetCatalogEntry): boolean {
  if (isCollectionAssetKey(entry.key)) return true;
  return (
    entry.surface === "assets" &&
    entry.cardinality === "singleton" &&
    !entry.comingSoon &&
    (entry.tracks.kind === "deliverable" || entry.tracks.kind === "plan_step")
  );
}

/**
 * Each item of the collection opens on its own page,
 * `/my-coaching/business/nova/assets/$assetKey/$itemId`, the asset page scoped to one
 * lineage: the collection's items are deliverable lineages (one LinkedIn post
 * each). Articles and client stories live in their own tables and are managed
 * where those live.
 */
export function hasItemPages(entry: AssetCatalogEntry): boolean {
  return isCollectionAssetKey(entry.key) && entry.tracks.kind === "deliverable";
}

/** A routine she runs every week (the weekly check-in), not an asset she builds. */
export function isRoutineAsset(key: AssetKey): boolean {
  return assetByKey(key).tracks.kind === "routine";
}

/**
 * The asset has no review step: it tracks a code-stamped deliverable, written
 * straight from validated coach input rather than by the model. It gets no
 * review-queue item and never has its `confirmedVersion` stamped — the row IS
 * the approval. The one predicate every surface that cares about "reviewed vs.
 * saved" reads, so the rule cannot drift between status derivation, the asset
 * detail read and the asset page.
 */
export function isReviewlessAsset(entry: AssetCatalogEntry): boolean {
  return (
    entry.tracks.kind === "deliverable" &&
    isCodeStampedDeliverableType(entry.tracks.type)
  );
}

/**
 * The coach approves the asset's versions: a model-written deliverable, or a
 * collection whose members are approved one by one. A Plan step's verdict and
 * the assessment are derived from answers, and a reviewless deliverable is
 * saved as given, so none of those was ever approved. The one predicate every
 * surface reads before it says "approved" rather than "saved" or "checked".
 */
export function hasApprovalStep(entry: AssetCatalogEntry): boolean {
  switch (entry.tracks.kind) {
    case "deliverable":
      return !isReviewlessAsset(entry);
    case "articles":
    case "stories":
      return true;
    case "plan_step":
    case "assessment":
    case "routine":
    case "none":
      return false;
    default: {
      const exhaustive: never = entry.tracks;
      throw new Error(`Unknown asset track: ${JSON.stringify(exhaustive)}`);
    }
  }
}

/** The entries that read from this one: every entry whose `dependsOn` names it. */
export function assetsFedBy(key: AssetKey): readonly AssetCatalogEntry[] {
  return ASSET_CATALOG.filter((entry) => entry.dependsOn.includes(key));
}

/** The course week the asset belongs to, via its component. */
export function assetCourseWeek(entry: AssetCatalogEntry): number | null {
  return entry.component === null
    ? null
    : CAS_COMPONENTS[entry.component].courseWeek;
}

/**
 * Everything NOVA can produce that deliberately has no catalog entry, with the
 * reason. The coverage test reads this: a new deliverable type, skill or Plan
 * step must either earn a card or be ruled out here, so the five definitions
 * this catalog replaces cannot quietly grow back.
 */
export const NOT_IN_CATALOG = {
  deliverableTypes: {
    platform_tags: "Dead type — the union member is its only occurrence.",
    video_scripts:
      "Authority video is hidden and gets no card; packs already saved still open.",
    mini_sprint_video_script:
      "A section of the Mini Sprint asset, with its own CTA there.",
    coach_story:
      "Raw material for other assets; it surfaces in What NOVA knows, not as a card.",
    article_proposals:
      "A batch of article ideas from the `propose-articles` run — an action on My content, not an asset.",
    coach_intake:
      "What NOVA learns in Getting to know you; it surfaces in What NOVA knows under About your business, not as a card. Trial coaches reach it through the gate.",
    mindset_profile:
      "The Mindset check's record; it surfaces in What NOVA knows under Who you are, not as a card. Trial coaches reach it through the gate.",
  },
  skills: {
    "coach-story": "Produces `coach_story`, which is a What NOVA knows record.",
    "offer-hooks":
      "Hooks are born approved and have no lifecycle; they render as a section of the Core Coaching Offer.",
    "mini-sprint-video-script": "A section of the Mini Sprint asset.",
    "authority-video":
      "Hidden from every skills menu and not carded; registered so old conversations still run. Its discovery-call map now lives in the Mini Sprint Day 5 close.",
    "success-story":
      "The CIH half of Client stories — a second action on that surface, reviewed by DCM admins rather than the coach.",
    "mindset-check":
      "Runs before the first outward asset and from Plan's trial-day offer, never from a card. Trial coaches reach it through the gate.",
  },
  planSteps: {
    getting_to_know_you:
      "Plan's first step, the opening part of the identity work. Its record, `coach_intake`, is no card; the step opens for trial coaches through the gate.",
  },
} as const satisfies {
  readonly deliverableTypes: Partial<Record<NovaDeliverableType, string>>;
  readonly skills: Partial<Record<SkillName, string>>;
  readonly planSteps: Partial<Record<PlanStepName, string>>;
};
